// The registry — the in-memory index of every discovered model. It is built
// from a list of *loaded* model files (a {@link Model} plus its path and raw
// source), which a discovery adapter supplies (Vite's glob in the browser,
// `fs` on the server — see discover.browser.ts / discover.node.ts). The
// registry is a deep module: callers get search, tag counts, lookup-by-id and
// source-by-id behind a tiny surface, and never touch how models were found.

import type { Model } from "../lib/types";
import { search, tagCounts, type SearchHit, type SearchOptions } from "../lib/search";

/** A model file as found by a discovery adapter. */
export interface LoadedModel {
  model: Model;
  /** Path relative to `src/models`, e.g. "buildings/watch-tower.ts". */
  path: string;
  /** The raw TypeScript source of the file — served verbatim over MCP. */
  source: string;
}

/** Structural guard: does an arbitrary export look like a built {@link Model}?
 *  (Every model is produced by `defineModel`, which yields this exact shape.) */
export function isModel(value: unknown): value is Model {
  const m = value as Model | null;
  return !!m && typeof m === "object"
    && typeof m.id === "string"
    && typeof m.name === "string"
    && Array.isArray(m.tags)
    && Array.isArray(m.variants) && m.variants.length > 0
    && Array.isArray(m.animations);
}

/** Pull every model export out of a loaded module's namespace object. */
export function modelsFromModule(mod: Record<string, unknown>): Model[] {
  return Object.values(mod).filter(isModel);
}

/** Normalise a discovery path down to one relative to `src/models`. */
export function relModelPath(path: string): string {
  const i = path.lastIndexOf("models/");
  return i === -1 ? path.replace(/^[./]+/, "") : path.slice(i + "models/".length);
}

export class Registry {
  readonly models: Model[];
  private readonly byId = new Map<string, Model>();
  private readonly sourceById = new Map<string, string>();
  private readonly pathById = new Map<string, string>();

  constructor(loaded: LoadedModel[]) {
    // Sort for stable ordering everywhere (listings, tests, the gallery).
    const sorted = [...loaded].sort((a, b) => a.model.id.localeCompare(b.model.id));
    for (const { model, path, source } of sorted) {
      if (this.byId.has(model.id)) {
        const first = this.pathById.get(model.id);
        throw new Error(
          `Duplicate model id "${model.id}" in ${path} and ${first}. ` +
          "Every model needs a unique id — rename one.",
        );
      }
      this.byId.set(model.id, model);
      this.sourceById.set(model.id, source);
      this.pathById.set(model.id, path);
    }
    this.models = sorted.map((l) => l.model);
  }

  get size(): number { return this.models.length; }
  get(id: string): Model | undefined { return this.byId.get(id); }
  source(id: string): string | undefined { return this.sourceById.get(id); }
  path(id: string): string | undefined { return this.pathById.get(id); }

  search(opts: SearchOptions): SearchHit[] { return search(this.models, opts); }
  tags(): { tag: string; count: number }[] { return tagCounts(this.models); }
}
